import React, { useState, useEffect, useRef } from 'react';
import { useAdminAuth } from './../context/AdminAuthContext';
import {
  getAdminProductTypes,
  getAdminBrands,
  getAdminFiles,
  uploadAdminFile,
  updateAdminFile,
  deleteAdminFile
} from './../api/adminApi';

export const AdminFiles = () => {
  const { token } = useAdminAuth();
  const [types, setTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [files, setFiles] = useState([]);

  const [selectedType, setSelectedType] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ show: false, type: 'success', message: '' });

  const fileInputRef = useRef(null);
  const [selectedFileObj, setSelectedFileObj] = useState(null);
  const [displayNameInput, setDisplayNameInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');

  useEffect(() => {
    async function loadTypes() {
      const result = await getAdminProductTypes(token);
      if (result.success) setTypes(result.data.productTypes);
    }
    loadTypes();
    // eslint-disable-next-line
  }, []);

  const handleTypeChange = async (e) => {
    const typeId = e.target.value;
    setSelectedType(typeId);
    setSelectedBrand('');
    setFiles([]);
    setBrands([]);

    if (!typeId) return;
    const result = await getAdminBrands(token, typeId);
    if (result.success) setBrands(result.data.brands);
  };

  const loadFiles = async (brandId, searchTerm = '') => {
    if (!brandId) return;
    setLoading(true);
    const result = await getAdminFiles(token, brandId, searchTerm);
    setLoading(false);

    if (result.success) {
      setFiles(result.data.files);
    } else {
      setToast({ show: true, type: 'error', message: result.message });
    }
  };

  const handleBrandChange = (e) => {
    const brandId = e.target.value;
    setSelectedBrand(brandId);
    setSearch('');
    loadFiles(brandId);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    loadFiles(selectedBrand, value);
  };

  const openUploadModal = () => {
    if (!selectedBrand) {
      setToast({ show: true, type: 'error', message: 'ابتدا نوع محصول و برند را انتخاب کنید.' });
      return;
    }
    setSelectedFileObj(null);
    setDisplayNameInput('');
    setDescriptionInput('');
    setShowUploadModal(true);
  };

  const handleFilePick = (e) => {
    const file = e.target.files[0];
    setSelectedFileObj(file);
    if (file && !displayNameInput) {
      setDisplayNameInput(file.name);
    }
  };

  const handleUpload = async () => {
    if (!selectedFileObj) {
      setToast({ show: true, type: 'error', message: 'لطفا یک فایل انتخاب کنید.' });
      return;
    }

    if (!selectedBrand) {
      setToast({ show: true, type: 'error', message: 'برند انتخاب نشده است. لطفا دوباره برند را انتخاب کنید.' });
      return;
    }

    setUploading(true);
    const result = await uploadAdminFile(token, {
      brandId: selectedBrand, // ⬅️ همیشه از همون state ای که با انتخاب دراپ‌داون آپدیت می‌شه استفاده می‌کنیم
      file: selectedFileObj,
      displayName: displayNameInput,
      description: descriptionInput
    });
    setUploading(false);

    if (result.success) {
      setToast({ show: true, type: 'success', message: result.data.message });
      setShowUploadModal(false);
      loadFiles(selectedBrand, search);
    } else {
      setToast({ show: true, type: 'error', message: result.message });
    }
  };

  const openEditModal = (file) => {
    setEditingFile(file);
    setDisplayNameInput(file.file_name);
    setDescriptionInput(file.description || '');
  };

  const handleEditSave = async () => {
    if (!displayNameInput.trim()) return;

    const result = await updateAdminFile(token, editingFile.id, displayNameInput, descriptionInput);

    if (result.success) {
      setToast({ show: true, type: 'success', message: result.data.message });
      setEditingFile(null);
      loadFiles(selectedBrand, search);
    } else {
      setToast({ show: true, type: 'error', message: result.message });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const result = await deleteAdminFile(token, deleteTarget.id);
    setDeleteTarget(null);

    if (result.success) {
      setToast({ show: true, type: 'success', message: result.data.message });
      loadFiles(selectedBrand, search);
    } else {
      setToast({ show: true, type: 'error', message: result.message });
    }
  };

  const selectStyle = {
    border: '1px solid white',
    backgroundColor: '#263238',
    color: 'white',
    borderRadius: 8,
    height: 40,
    padding: '0 10px'
  };

  return (
    <div dir="rtl" style={{ padding: 24 }}>
      <h2 className="w3-text-white" style={{ marginBottom: 20 }}>مدیریت فایل‌ها</h2>

      <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ minWidth: 220 }}>
          <label className="w3-text-white">نوع محصول</label>
          <select value={selectedType} onChange={handleTypeChange} className="w3-select" style={selectStyle}>
            <option value="">انتخاب کنید...</option>
            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>

        <div style={{ minWidth: 220 }}>
          <label className="w3-text-white">برند</label>
          <select value={selectedBrand} onChange={handleBrandChange} disabled={!selectedType} className="w3-select" style={selectStyle}>
            <option value="">انتخاب کنید...</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>

        <div style={{ minWidth: 220 }}>
          <label className="w3-text-white">جستجو در فایل‌ها</label>
          <input
            type="text"
            value={search}
            onChange={handleSearchChange}
            disabled={!selectedBrand}
            placeholder="جستجو..."
            className="w3-input"
            style={selectStyle}
          />
        </div>

        <button
          onClick={openUploadModal}
          className="w3-button w3-round-xxlarge"
          style={{ color: '#15ff00', border: '2px solid #2980b9', backgroundColor: 'transparent', display: 'flex', alignItems: 'center', height: 40 }}
        >
          <i className="material-icons w3-margin-left">upload_file</i>
          آپلود فایل جدید
        </button>
      </div>

      {/* نمایش برند فعلاً انتخاب‌شده، برای اطمینان بصری قبل از آپلود */}
      {selectedBrand && (
        <p className="w3-text-light-grey" style={{ fontSize: 13, marginBottom: 10 }}>
          در حال مدیریت فایل‌های برند: <b style={{ color: '#4FC3F7' }}>{brands.find(b => String(b.id) === String(selectedBrand))?.name}</b>
        </p>
      )}

      <div className="w3-card-4 w3-round-large" style={{ backgroundColor: '#263238', padding: 10 }}>
        {!selectedBrand ? (
          <p className="w3-text-grey w3-center" style={{ padding: 30 }}>نوع محصول و برند را انتخاب کنید.</p>
        ) : loading ? (
          <p className="w3-text-grey w3-center" style={{ padding: 30 }}>در حال بارگذاری...</p>
        ) : files.length === 0 ? (
          <p className="w3-text-grey w3-center" style={{ padding: 30 }}>فایلی یافت نشد.</p>
        ) : (
          <table className="w3-table w3-text-white" style={{ width: '100%' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #37474F' }}>
                <th style={{ textAlign: 'right', padding: 10 }}>#</th>
                <th style={{ textAlign: 'right', padding: 10 }}>نام نمایشی</th>
                <th style={{ textAlign: 'right', padding: 10 }}>نام فایل روی هاست دانلود</th>
                <th style={{ textAlign: 'left', padding: 10 }}>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.id} style={{ borderBottom: '1px solid #263238' }}>
                  <td style={{ padding: 10 }}>{f.id}</td>
                  <td style={{ padding: 10 }}>{f.file_name}</td>
                  <td style={{ padding: 10, color: '#90A4AE', fontSize: 12 }}>{f.file_path}</td>
                  <td style={{ padding: 10, textAlign: 'left' }}>
                    <button onClick={() => openEditModal(f)} className="w3-button" style={{ color: '#4FC3F7' }}>
                      <i className="material-icons">edit</i>
                    </button>
                    <button onClick={() => setDeleteTarget(f)} className="w3-button" style={{ color: '#ff0000' }}>
                      <i className="material-icons">delete</i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* مودال آپلود */}
      {showUploadModal && (
        <div className="w3-modal" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999 }}>
          <div className="w3-card-4 w3-animate-zoom" style={{ backgroundColor: '#263238', borderRadius: 16, width: 420, maxWidth: '90%', overflow: 'hidden' }}>
            <div className="w3-container w3-padding" style={{ borderBottom: '1px solid #37474F' }}>
              <span onClick={() => setShowUploadModal(false)} className="w3-button w3-display-topright" style={{ color: 'white' }}>&times;</span>
              <h4 className="w3-text-white" style={{ margin: 0 }}>آپلود فایل جدید</h4>
            </div>
            <div className="w3-container w3-padding">
              <p className="w3-text-light-grey" style={{ fontSize: 12, marginBottom: 10 }}>
                برند مقصد: <b style={{ color: '#4FC3F7' }}>{brands.find(b => String(b.id) === String(selectedBrand))?.name}</b>
              </p>

              <label className="w3-text-white">انتخاب فایل</label>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFilePick}
                className="w3-input w3-text-white"
                style={{ border: '1px solid white', backgroundColor: 'transparent', borderRadius: 8, height: 40, padding: '6px' }}
              />

              <label className="w3-text-white" style={{ marginTop: 10, display: 'block' }}>نام نمایشی (اختیاری)</label>
              <input
                type="text"
                value={displayNameInput}
                onChange={(e) => setDisplayNameInput(e.target.value)}
                className="w3-input"
                style={{ border: '1px solid white', backgroundColor: 'transparent', color: 'white', borderRadius: 8, height: 40 }}
              />

              <label className="w3-text-white" style={{ marginTop: 10, display: 'block' }}>توضیحات (اختیاری)</label>
              <textarea
                value={descriptionInput}
                onChange={(e) => setDescriptionInput(e.target.value)}
                className="w3-input"
                style={{ border: '1px solid white', backgroundColor: 'transparent', color: 'white', borderRadius: 8, minHeight: 60 }}
              />
            </div>
            <div className="w3-container w3-padding" style={{ paddingBottom: 16 }}>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="w3-button w3-block"
                style={{ color: '#15ff00', border: '2px solid #2980b9', backgroundColor: 'transparent', borderRadius: 10 }}
              >
                {uploading ? 'در حال آپلود به هاست دانلود...' : 'آپلود و ذخیره'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* مودال ویرایش */}
      {editingFile && (
        <div className="w3-modal" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999 }}>
          <div className="w3-card-4 w3-animate-zoom" style={{ backgroundColor: '#263238', borderRadius: 16, width: 400, maxWidth: '90%', overflow: 'hidden' }}>
            <div className="w3-container w3-padding" style={{ borderBottom: '1px solid #37474F' }}>
              <span onClick={() => setEditingFile(null)} className="w3-button w3-display-topright" style={{ color: 'white' }}>&times;</span>
              <h4 className="w3-text-white" style={{ margin: 0 }}>ویرایش فایل</h4>
            </div>
            <div className="w3-container w3-padding">
              <label className="w3-text-white">نام نمایشی</label>
              <input
                type="text"
                value={displayNameInput}
                onChange={(e) => setDisplayNameInput(e.target.value)}
                className="w3-input"
                style={{ border: '1px solid white', backgroundColor: 'transparent', color: 'white', borderRadius: 8, height: 40 }}
              />
              <label className="w3-text-white" style={{ marginTop: 10, display: 'block' }}>توضیحات</label>
              <textarea
                value={descriptionInput}
                onChange={(e) => setDescriptionInput(e.target.value)}
                className="w3-input"
                style={{ border: '1px solid white', backgroundColor: 'transparent', color: 'white', borderRadius: 8, minHeight: 60 }}
              />
            </div>
            <div className="w3-container w3-padding" style={{ paddingBottom: 16 }}>
              <button onClick={handleEditSave} className="w3-button w3-block" style={{ color: '#15ff00', border: '2px solid #2980b9', backgroundColor: 'transparent', borderRadius: 10 }}>
                ذخیره تغییرات
              </button>
            </div>
          </div>
        </div>
      )}

      {/* تایید حذف */}
      {deleteTarget && (
        <div className="w3-modal" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999 }}>
          <div className="w3-card-4 w3-animate-zoom" style={{ backgroundColor: '#263238', borderRadius: 16, width: 340, maxWidth: '90%', overflow: 'hidden' }}>
            <div className="w3-container w3-padding" style={{ backgroundColor: '#ff000022', borderBottom: '2px solid #ff0000' }}>
              <h4 className="w3-text-white" style={{ margin: 0 }}>تایید حذف</h4>
            </div>
            <div className="w3-container w3-padding">
              <p className="w3-text-white">آیا از حذف فایل «{deleteTarget.file_name}» مطمئن هستید؟ فایل از روی هاست دانلود هم حذف می‌شود.</p>
            </div>
            <div className="w3-container w3-padding" style={{ paddingBottom: 16, display: 'flex', gap: 10 }}>
              <button onClick={handleDelete} className="w3-button" style={{ flex: 1, color: 'white', backgroundColor: '#ff0000', borderRadius: 10 }}>
                بله، حذف شود
              </button>
              <button onClick={() => setDeleteTarget(null)} className="w3-button" style={{ flex: 1, color: '#15ff00', border: '2px solid #15ff00', backgroundColor: 'transparent', borderRadius: 10 }}>
                انصراف
              </button>
            </div>
          </div>
        </div>
      )}

      {/* پیام موفقیت/خطا */}
      {toast.show && (
        <div className="w3-modal" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999 }}>
          <div className="w3-card-4 w3-animate-zoom" style={{ backgroundColor: '#263238', borderRadius: 16, width: 320, maxWidth: '90%', overflow: 'hidden' }}>
            <div
              className="w3-container w3-padding"
              style={{ backgroundColor: toast.type === 'success' ? '#15ff0022' : '#ff000022', borderBottom: `2px solid ${toast.type === 'success' ? '#15ff00' : '#ff0000'}` }}
            >
              <span onClick={() => setToast({ ...toast, show: false })} className="w3-button w3-display-topright" style={{ color: 'white' }}>&times;</span>
              <h4 className="w3-text-white" style={{ margin: 0 }}>{toast.type === 'success' ? '✅ موفقیت' : '❌ خطا'}</h4>
            </div>
            <div className="w3-container w3-padding"><p className="w3-text-white">{toast.message}</p></div>
            <div className="w3-container w3-padding" style={{ paddingBottom: 16 }}>
              <button
                onClick={() => setToast({ ...toast, show: false })}
                className="w3-button w3-block"
                style={{ color: toast.type === 'success' ? '#15ff00' : '#ff0000', border: `2px solid ${toast.type === 'success' ? '#15ff00' : '#ff0000'}`, backgroundColor: 'transparent', borderRadius: 10 }}
              >
                باشه
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};